import React from "react";
import styled from "styled-components";

// A desktop icon: emoji glyph + label, single click selects, double click
// opens. Selection is local — clicking the desktop elsewhere deselects via
// the blur handler, like the real thing. Selection happens on pointer DOWN
// (not click): Win95 highlights the label the instant you press, so a drag
// carries a navy label the whole way rather than lighting up on drop.
function DesktopIcon({ icon, label, onOpen }) {
  const [selected, setSelected] = React.useState(false);
  // "Double click" counted by hand from click events: browsers never
  // synthesize dblclick from a touch double-tap. Mouse behavior is
  // unchanged — the second click of a real double-click still arrives
  // as its own click first.
  const lastTapRef = React.useRef(0);
  const onClick = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 500) {
      lastTapRef.current = 0;
      setSelected(false);
      if (onOpen) onOpen();
    } else {
      lastTapRef.current = now;
    }
  };
  return (
    <Root
      type="button"
      data-selected={selected ? "true" : undefined}
      onPointerDown={(e) => {
        if (e.button === 0) setSelected(true);
      }}
      onBlur={() => setSelected(false)}
      onClick={onClick}
    >
      <Glyph aria-hidden="true">{icon}</Glyph>
      <Label data-selected={selected ? "true" : undefined}>{label}</Label>
    </Root>
  );
}

const Root = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 76px;
  padding: 4px 2px;
  background: transparent;
  border: none;
  box-shadow: none;
  min-width: 0;
  min-height: 0;
  font: inherit;
  cursor: default;
  /* Two quick taps open — don't spend the second on double-tap zoom. */
  touch-action: manipulation;

  &&:not(:disabled):hover,
  &&:not(:disabled):active {
    box-shadow: none;
    text-shadow: none;
  }
  &&:focus {
    outline: none;
  }
  &&:focus-visible span:last-child {
    background: #00007b;
    outline: 1px dotted #ffff7b;
  }
`;

const Glyph = styled.span`
  font-size: 30px;
  line-height: 1;
  filter: drop-shadow(1px 1px 0 rgba(0, 0, 0, 0.4));
`;

const Label = styled.span`
  color: #fff;
  font-size: 13px;
  line-height: 1.15;
  text-align: center;
  padding: 1px 3px;
  text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.7);

  &[data-selected="true"] {
    background: #00007b;
    text-shadow: none;
    outline: 1px dotted #ffff7b;
  }
`;

export default DesktopIcon;
